import { useMutation, useQuery } from '@apollo/client/react';
import { Spinner } from '@optools/components/loading/spinner';
import { displayApolloError } from '@optools/components/notifications/apollo';
import { Button } from '@optools/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@optools/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@optools/components/ui/popover';
import { ASSIGN_EVI_POD } from '@optools/services/operator-gateway/btc/mutations';
import { EVI_PODS } from '@optools/services/operator-gateway/btc/queries';
import {
  EviPodInfo,
  MediaPipeline,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { Check, ChevronDownIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { isEVIDisabled } from '../../../utils/evi';

export type EVIPodsSelectProps = Pick<MediaPipeline, 'eviPod' | 'status'> & {
  mediaId: string;
};

export function EVIPodsSelect({ eviPod, mediaId, status }: EVIPodsSelectProps) {
  const [open, setOpen] = useState(false);
  const [selectedPod, setSelectedPod] = useState<MediaPipeline['eviPod']>(eviPod);

  useEffect(() => {
    if (eviPod) setSelectedPod(eviPod);
  }, [eviPod]);

  const { data, loading: eviPodsLoading } = useQuery(EVI_PODS);

  const [assignEviPod, { loading: assignEviPodLoading }] = useMutation(ASSIGN_EVI_POD);

  const handleAssignEviPod = (pod: EviPodInfo) => {
    setSelectedPod(pod);

    assignEviPod({
      onCompleted: ({ assignEviPod }) => {
        const { mediaId, reason, success } = assignEviPod;
        if (!success) {
          toast.error('EVI Pod Assignment Failed', {
            description: (
              <>
                <p className="mb-1">{reason}</p>
                <p>Media ID: {mediaId}</p>
              </>
            ),
            duration: Infinity,
          });
          setSelectedPod(eviPod);
        }
      },
      onError: (error) => {
        displayApolloError({
          error,
          options: { description: `Media ID: ${mediaId}` },
          title: 'EVI Pod Assignment Failed',
        });
        setSelectedPod(eviPod);
      },
      variables: { eviPodName: pod.name, mediaId },
    });
  };

  const podVendorMap = useMemo(() => {
    return (data?.eviPods ?? []).reduce<Record<string, EviPodInfo[]>>((acc, pod) => {
      if (!acc[pod.vendor]) {
        acc[pod.vendor] = [];
      }
      acc[pod.vendor].push(pod);
      return acc;
    }, {});
  }, [data]);

  const loading = eviPodsLoading || assignEviPodLoading;
  const disabled = loading || isEVIDisabled(status);

  return (
    <div className="flex flex-col items-start gap-2">
      <span className="font-bold">Pod</span>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            aria-expanded={open}
            aria-label="EVI Pod"
            className="w-50 justify-between text-xs peer-disabled:cursor-not-allowed"
            disabled={disabled}
            role="combobox"
            variant="outline">
            {selectedPod?.name ?? 'Select EVI Pod'}
            {loading ? (
              <Spinner className="ml-auto size-4" />
            ) : (
              <ChevronDownIcon className="ml-2 size-4 shrink-0" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-50 p-0">
          <Command>
            <CommandInput placeholder="Search pods..." />
            <CommandList className="max-h-40">
              <CommandEmpty>No EVI pod found.</CommandEmpty>
              {Object.keys(podVendorMap).map((vendor) => (
                <CommandGroup key={vendor} heading={vendor}>
                  {podVendorMap[vendor].map((pod) => {
                    const isSelected = selectedPod?.name === pod.name;
                    return (
                      <CommandItem
                        key={pod.id}
                        disabled={!pod.active || isSelected}
                        value={pod.name}
                        onSelect={() => {
                          handleAssignEviPod(pod);
                          setOpen(false);
                        }}>
                        {pod.name}
                        {isSelected && (
                          <Check className="ml-auto size-4" data-testid="pod-checked-icon" />
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
